/**
 * The guided take: the page runs the session, you just make the sounds.
 *
 * SPEC1.md's Phase 6 acceptance is "a table of 7 sounds performed 5x each,
 * classified correctly >=4/5". Six recordings have now failed to produce it, and
 * the last one explains why it was never a matter of trying harder. Marking by
 * hand requires you to look at the screen and press a button — so in a
 * sixty-nine second take the face was out of frame for forty of them, and the
 * transcript is the speaker reading the instructions aloud. Every gesture in
 * that hole was classified with no vision behind it.
 *
 * Marking is therefore the wrong job for the person making the sounds. The take
 * is scripted: it says what to do, marks the moment itself, and asks nothing of
 * you except to look at the camera and perform. Which is also the only
 * arrangement in which the vision gate is being tested rather than defeated.
 *
 * ### Three blocks, and the third is the one nobody would think to record
 *
 *   vowels    rest / ah / eee / oo, for the Phase 1 acceptance criteria, which
 *             have also never been scored on a take that contained them
 *   gestures  the spec's table, five repetitions each
 *   control   twenty seconds of sitting still while something else makes noise
 *
 * The control block settles `ACTIVITY_MIN`, the threshold that decides whether a
 * detected sound was the speaker's at all. It has been a starting guess for
 * three phases for want of exactly this: a recording in which the correct answer
 * is known in advance to be "nothing here is mine".
 *
 * ### Driven from the frame loop, not from setTimeout
 *
 * A chain of timers drifts, and worse, keeps firing while the tab is hidden —
 * so a take that got backgrounded would mark five trills against a recording of
 * silence. Ticking from the render loop means the script advances on the same
 * clock as everything it is labelling, and stops when the page does.
 */

/** Sustained sounds: held long enough for the hysteresis to commit. */
const SUSTAINED = [
  ['trill', 'a lip trill — brrrrr'],
  ['whistle', 'whistle, any note'],
  ['sss', 'a long sssss'],
  ['growl', 'growl, low and rough'],
  ['raspberry', 'a raspberry'],
];

/** Transients: over in milliseconds, so held briefly and repeated. */
const TRANSIENT = [
  ['papa', 'say "papa" — hard p'],
  ['clap', 'one sharp clap'],
];

/** Vowels, for the Phase 1 criteria. Held, mouth clearly shaped. */
const VOWELS = [
  ['rest', 'mouth closed, relaxed'],
  ['ah', 'open wide — aaah'],
  ['eee', 'wide smile — eeee'],
  ['oo', 'tight pucker — oooo'],
  ['meep', 'say "meep" — small mouth'],
];

export const REPS = 5;

const say = (text, dur) => ({ kind: 'say', text, dur });
const rest = (dur, next) => ({ kind: 'rest', text: next ? `${next}…` : 'ready', dur });
const doIt = (label, text, dur) => ({ kind: 'do', label, text, dur });

/**
 * Build the script.
 *
 * @param {object} [o]
 * @param {number} [o.reps] repetitions per gesture
 * @param {boolean} [o.control] include the still-and-silent block
 * @returns {object[]}
 */
export function buildScript({ reps = REPS, control = true } = {}) {
  const steps = [say('look at the camera — it will tell you what to do', 4000)];

  steps.push(say('vowels first', 2000));
  for (const [label, text] of VOWELS) {
    steps.push(rest(1200, text));
    steps.push(doIt(label, text, 2000));
  }

  steps.push(say('now sounds — five of each', 2500));
  for (const [label, text] of SUSTAINED) {
    steps.push(say(text, 2000));
    for (let i = 0; i < reps; i++) {
      steps.push(rest(1000, `${i + 1} of ${reps}`));
      // Long enough to clear HYSTERESIS_MS several times over, and — for the
      // trill — long enough for the envelope FFT to fit its 683 ms window
      // inside with room for the stability run the detector needs.
      steps.push(doIt(label, `${text}  (${i + 1}/${reps})`, 2000));
    }
  }
  for (const [label, text] of TRANSIENT) {
    steps.push(say(text, 2000));
    for (let i = 0; i < reps; i++) {
      steps.push(rest(900, `${i + 1} of ${reps}`));
      steps.push(doIt(label, `${text}  (${i + 1}/${reps})`, 900));
    }
  }

  if (control) {
    steps.push(
      say(
        'last part: sit still and make no sound. play music if you can — that is the point',
        6000,
      ),
    );
    // Marked, because the analyzer works in labelled segments and this segment
    // has the most important label in the whole take: the answer is known, and
    // the answer is "nothing detected here belongs to the speaker".
    steps.push(doIt('still', 'still and silent — look at the camera', 20_000));
  }

  steps.push(say('done — stopping', 2500));
  return steps;
}

export class Guide {
  /**
   * @param {object} o
   * @param {(label: string) => void} [o.onMark] called once as a `do` begins
   * @param {(state: object) => void} [o.onStep] called when the step changes
   * @param {() => void} [o.onDone]
   * @param {object[]} [o.script]
   */
  constructor({ onMark, onStep, onDone, script } = {}) {
    this.onMark = onMark ?? (() => {});
    this.onStep = onStep ?? (() => {});
    this.onDone = onDone ?? (() => {});
    this.script = script ?? buildScript();
    this.index = -1;
    this._startedAt = 0;
    this._stepAt = 0;
    this.running = false;
  }

  /** Total length, ms. What the button should say before you commit to it. */
  get durationMs() {
    return this.script.reduce((sum, s) => sum + s.dur, 0);
  }

  start(now = performance.now()) {
    this.running = true;
    this.index = -1;
    this._startedAt = now;
    this._advance(now);
  }

  stop() {
    this.running = false;
    this.index = -1;
    this.onStep(null);
  }

  _advance(now) {
    this.index++;
    this._stepAt = now;
    const step = this.script[this.index];
    if (!step) {
      this.running = false;
      this.onStep(null);
      this.onDone();
      return;
    }
    // The mark goes down as the step begins, which is the moment the speaker is
    // being told to start — not when they were asked to get ready, and not when
    // it ended.
    if (step.kind === 'do') this.onMark(step.label);
    this.onStep(this.state(now));
  }

  /**
   * Advance the script. Call once per frame.
   *
   * @param {number} now performance.now()
   * @returns {?object} the current state, or null when not running
   */
  tick(now) {
    if (!this.running) return null;
    const step = this.script[this.index];
    if (step && now - this._stepAt >= step.dur) {
      this._advance(now);
      return this.running ? this.state(now) : null;
    }
    return this.state(now);
  }

  /** What to put on screen. */
  state(now = performance.now()) {
    const step = this.script[this.index];
    if (!step) return null;
    const elapsed = now - this._stepAt;
    return {
      step,
      index: this.index,
      total: this.script.length,
      /** 0..1 through this step — the thing to draw as a bar. */
      progress: Math.min(1, elapsed / step.dur),
      remaining: Math.max(0, step.dur - elapsed),
      /** Whole take, 0..1. */
      overall: Math.min(1, (now - this._startedAt) / this.durationMs),
      /** True while the speaker should be making the sound. */
      active: step.kind === 'do',
    };
  }
}

export default Guide;
